import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@/shared/components/ui/card';
import { useState, useMemo, useEffect, useRef } from 'react';
import { auth } from '@/core/firebase/config';
import useGetPreferredOffice from '@/shared/hooks/useGetPreferredOffice';
import useGetOffice from '@/features/office/hooks/useGetOffice';
import useGetEvents from '@/features/events/hooks/useGetEvents';
import { addDays, endOfWeek, startOfWeek } from 'date-fns';
import {
  getHourInTimezone,
  getDateKeyInTimezone,
  isTimestampOnDateKey,
} from '@/shared/utils/dates';
import Booking from '@/features/booking/components/BookingUpdated';
import type { Booking as BookingRecord } from '@/features/booking/types';
import useGetOfficeLayout from '@/features/office/hooks/useGetOfficeLayout';
import useGetAllBookings from '@/features/booking/hooks/useGetAllBookings';
import useBookMeetingRoom from '@/features/booking/hooks/useBookMeetingRoom';
import useCancelMeetingRoomBook from '@/features/booking/hooks/useCancelMeetingRoomBook';
import BookingPanel from '@/features/booking/components/BookingPanel';
import useBookDesk from '@/features/booking/hooks/useBookDesk';
import useCancelDeskBook from '@/features/booking/hooks/useCancelDeskBook';
import useBookOverflow from '@/features/booking/hooks/useBookOverflow';
import { isOfficeFullyBooked } from '@/features/booking/utils/office-availability';
import {
  DEFAULT_FLOOR_ID,
  findFloorForResource,
  getFreeDesksByFloor,
  resolveOfficeFloors,
} from '@/features/office/utils/floors';
import { FloorPicker } from '@/features/booking/components/FloorPicker';
import { WeekCalendar } from '@/features/booking/components/WeekCalendar';
import { getDayMarks, getEventDateKeys } from '@/features/booking/utils/day-marks';
import { ViewModeSwitch } from '@/features/booking/components/ViewModeSwitch';
import { DesklessBookings } from '@/features/booking/components/DesklessBookings';
import { motion, AnimatePresence } from 'motion/react';
import { BookingInfoBox } from '@/features/booking/components/BookingInfoBox';
import useGetPreferredViewMode from '@/shared/hooks/useGetPreferredViewMode';
import useSavePreferredViewMode, { ViewMode } from '@/shared/hooks/useSavePreferredViewMode';
import { OfficeInfoDialog } from '@/features/office/components/OfficeInfoDialog';
import { SuppliesAndSuggestionsDialog } from '@/features/office/components/SuppliesAndSuggestionsDialog';
import { Info } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useNavigate } from '@tanstack/react-router';
import useSavePreferredOffice from '@/shared/hooks/useSavePreferredOffice';
import { BookingPageSkeleton } from '@/shared/components/feedback/skeletons';
import { ErrorState } from '@/shared/components/feedback';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import useCanManageOffice from '@/features/user/hooks/useCanManageOffice';

/**
 * Slides a booking side panel in from the right. Use `transform`, not motion's `x`: `x` is
 * animated on the main thread, which is busy re-rendering the page and the map while a panel
 * opens or closes, whereas `transform` runs on the compositor.
 */
const SIDE_PANEL_SLIDE = {
  initial: { transform: 'translateX(100%)' },
  animate: { transform: 'translateX(0%)' },
  exit: { transform: 'translateX(100%)' },
  transition: { duration: 0.3, ease: 'easeOut' },
} as const;

function bookingDaySignature(bookings: BookingRecord[]) {
  let signature = '';
  for (const booking of bookings) {
    signature += [
      booking.id,
      booking.userEmail,
      booking.userName,
      booking.userImage,
      booking.resourceId ?? '',
      booking.startTime ?? '',
      booking.endTime ?? '',
      booking.isGuest ? '1' : '0',
      booking.guestName ?? '',
      booking.guestEmail ?? '',
      booking.facilitiesNotified ? '1' : '0',
    ].join('\0');
    signature += '\n';
  }
  return signature;
}

/** Keep the same array when another day in the week changes but this day did not. */
function useStableDayBookings(bookings: BookingRecord[], dateKey: string, timezone: string) {
  const filtered = useMemo(
    () => bookings.filter(booking => isTimestampOnDateKey(booking.date, dateKey, timezone)),
    [bookings, dateKey, timezone]
  );
  const signature = bookingDaySignature(filtered);
  const stableRef = useRef(filtered);
  const signatureRef = useRef(signature);
  if (signatureRef.current !== signature) {
    signatureRef.current = signature;
    stableRef.current = filtered;
  }
  return stableRef.current;
}

export const Route = createFileRoute('/booking/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    // Validate and transform search parameters
    return {
      resourceId: (search.resourceId as string) || undefined,
      officeId: (search.officeId as string) || undefined, // Office ID from booking link
      date: (search.date as string) || undefined, // YYYY-MM-DD format
      startTime: (search.startTime as string) || undefined, // HH:mm format
      endTime: (search.endTime as string) || undefined, // HH:mm format
    };
  },
});

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  // Normalize any Date to noon local time so that extractDateParts returns
  // the correct calendar day in every timezone within ±12h of the user.
  const toNoonLocal = (d: Date): Date => {
    const normalized = new Date(d);
    normalized.setHours(12, 0, 0, 0);
    return normalized;
  };

  const [selectedDate, setSelectedDateRaw] = useState<Date>(() => {
    if (searchParams.date) {
      try {
        const date = new Date(searchParams.date + 'T12:00:00');
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch {
        // Invalid date, fall back to default
      }
    }

    const now = new Date();
    const hour = now.getHours();
    if (hour >= 18) {
      return toNoonLocal(addDays(now, 1));
    }
    return toNoonLocal(now);
  });

  const setSelectedDate = (date: Date) => setSelectedDateRaw(toNoonLocal(date));
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [selectedMeetingRoomForBooking, setSelectedMeetingRoomForBooking] = useState<{
    id: string;
    name: string;
  } | null>(() => {
    // Initialize from search params if resourceId is provided
    if (searchParams.resourceId) {
      return {
        id: searchParams.resourceId,
        name: '', // Will be populated from layout data
      };
    }
    return null;
  });
  const [selectedDeskForBooking, setSelectedDeskForBooking] = useState<{
    id: string;
    name: string;
  } | null>(() => {
    // Initialize from search params if resourceId is provided
    if (searchParams.resourceId) {
      return {
        id: searchParams.resourceId,
        name: '', // Will be populated from layout data
      };
    }
    return null;
  });
  const [showOverflowBookingPanel, setShowOverflowBookingPanel] = useState(false);
  const [officeInfoDialogOpen, setOfficeInfoDialogOpen] = useState(false);
  // Store initial booking params to pass to BookingPanel
  const [initialBookingParams] = useState({
    startTime: searchParams.startTime,
    endTime: searchParams.endTime,
  });
  // Track the resource ID that the initial params were intended for
  const [initialResourceId] = useState(searchParams.resourceId);
  const [urlCleaned, setUrlCleaned] = useState(false);
  // Track whether the initial params have already been used (i.e., panel has been closed once)
  const [initialParamsUsed, setInitialParamsUsed] = useState(false);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(
    searchParams.officeId || null
  );
  const [selectedFloorId, setSelectedFloorId] = useState(DEFAULT_FLOOR_ID);
  const floorChoiceOfficeId = useRef<string | null>(null);
  const userPickedFloor = useRef(false);
  const appliedResourceId = useRef<string | null>(null);

  const currentUser = auth.currentUser;
  const { user: appUser } = useAuth();
  const { preferredOffice, isLoading: preferredOfficeLoading } = useGetPreferredOffice(
    currentUser?.uid
  );
  const effectiveOfficeId = selectedOfficeId || preferredOffice;
  const { canManage, isLoading: canManageLoading } = useCanManageOffice(
    currentUser?.uid,
    effectiveOfficeId
  );
  const canRemoveOthers = canManage && !canManageLoading;
  const savePreferredOffice = useSavePreferredOffice();

  const {
    office,
    loading: officeLoading,
    error: officeError,
  } = useGetOffice(effectiveOfficeId || '');
  const officeTimezone = office?.timezone || 'UTC';

  const savePreferredViewMode = useSavePreferredViewMode();

  useEffect(() => {
    if (searchParams.officeId) {
      if (selectedOfficeId !== searchParams.officeId) {
        setSelectedOfficeId(searchParams.officeId);
      }
      if (currentUser?.uid && preferredOffice !== searchParams.officeId) {
        savePreferredOffice(currentUser.uid, searchParams.officeId);
      }
    } else if (preferredOffice && selectedOfficeId !== preferredOffice) {
      setSelectedOfficeId(preferredOffice);
    }
  }, [
    searchParams.officeId,
    preferredOffice,
    selectedOfficeId,
    currentUser?.uid,
    savePreferredOffice,
  ]);

  useEffect(() => {
    if (!searchParams.officeId && preferredOffice) {
      setSelectedOfficeId(prev => prev ?? preferredOffice);
    }
  }, [searchParams.officeId, preferredOffice]);

  // Correct the default date once the office timezone is known
  const dateAdjustedForTz = useRef(false);
  useEffect(() => {
    if (dateAdjustedForTz.current || searchParams.date || !office?.timezone) return;
    dateAdjustedForTz.current = true;
    const now = new Date();
    const hourInOffice = getHourInTimezone(now, officeTimezone);
    if (hourInOffice >= 18) {
      setSelectedDate(addDays(now, 1));
    }
  }, [office?.timezone]);

  useEffect(() => {
    if (
      !urlCleaned &&
      searchParams.officeId &&
      !searchParams.resourceId &&
      effectiveOfficeId === searchParams.officeId &&
      office
    ) {
      navigate({
        to: '/booking',
        search: {
          resourceId: undefined,
          officeId: undefined,
          date: undefined,
          startTime: undefined,
          endTime: undefined,
        },
        replace: true,
      });
      setUrlCleaned(true);
    }
  }, [
    urlCleaned,
    searchParams.officeId,
    searchParams.resourceId,
    effectiveOfficeId,
    office,
    navigate,
  ]);

  // Clean up URL params after side panel opens from a booking link
  useEffect(() => {
    if (
      !urlCleaned &&
      (selectedMeetingRoomForBooking || selectedDeskForBooking) &&
      searchParams.resourceId
    ) {
      // URL was opened with booking link params, clean it up now
      navigate({
        to: '/booking',
        search: {
          resourceId: undefined,
          officeId: undefined,
          date: undefined,
          startTime: undefined,
          endTime: undefined,
        },
        replace: true,
      });
      setUrlCleaned(true);
    }
  }, [
    selectedMeetingRoomForBooking,
    selectedDeskForBooking,
    searchParams.resourceId,
    urlCleaned,
    navigate,
  ]);

  // Mark initial params as used when the panel is closed
  useEffect(() => {
    if (!selectedMeetingRoomForBooking && !selectedDeskForBooking && initialResourceId) {
      // Panel was just closed after being opened from a booking link
      setInitialParamsUsed(true);
    }
  }, [selectedMeetingRoomForBooking, selectedDeskForBooking, initialResourceId]);

  // Only pass initial booking params if they haven't been used yet
  const activeInitialBookingParams =
    !initialParamsUsed &&
    (selectedMeetingRoomForBooking?.id === initialResourceId ||
      selectedDeskForBooking?.id === initialResourceId)
      ? initialBookingParams
      : { startTime: undefined, endTime: undefined };

  const motionDivRef = useRef<HTMLDivElement>(null);

  // Handle browser back button to close side panels
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // If a panel is open, close it instead of navigating back
      if (selectedMeetingRoomForBooking || selectedDeskForBooking || showOverflowBookingPanel) {
        event.preventDefault();
        setSelectedMeetingRoomForBooking(null);
        setSelectedDeskForBooking(null);
        setShowOverflowBookingPanel(false);
        // Push a new state to prevent actual navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Add a history entry when a panel opens
    if (selectedMeetingRoomForBooking || selectedDeskForBooking || showOverflowBookingPanel) {
      window.history.pushState(null, '', window.location.href);
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedMeetingRoomForBooking, selectedDeskForBooking, showOverflowBookingPanel]);

  // Handle ESC key to close side panels
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (selectedMeetingRoomForBooking || selectedDeskForBooking || showOverflowBookingPanel) {
          event.preventDefault();
          setSelectedMeetingRoomForBooking(null);
          setSelectedDeskForBooking(null);
          setShowOverflowBookingPanel(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedMeetingRoomForBooking, selectedDeskForBooking, showOverflowBookingPanel]);

  const { layoutData: officeLayout } = useGetOfficeLayout(effectiveOfficeId, selectedFloorId);

  // A booking link carries a desk or room id. That id picks the floor, because each resource lives on one floor.
  useEffect(() => {
    if (!searchParams.resourceId || !office) return;
    if (appliedResourceId.current === searchParams.resourceId) return;
    const officeFloors = resolveOfficeFloors(office);
    const floor = findFloorForResource(officeFloors, searchParams.resourceId);
    if (!floor) return;

    const desk = floor.layout.placedDesks?.[searchParams.resourceId];
    const room = floor.layout.placedMeetingRooms?.[searchParams.resourceId];
    userPickedFloor.current = true;
    floorChoiceOfficeId.current = office.id;
    appliedResourceId.current = searchParams.resourceId;
    setSelectedFloorId(floor.id);

    if (room) {
      const roomName = room.label?.trim() || `Room ${searchParams.resourceId}`;
      setSelectedMeetingRoomForBooking({ id: searchParams.resourceId, name: roomName });
      setSelectedDeskForBooking(null);
    } else if (desk) {
      const deskName = desk.label?.trim() || `Desk ${searchParams.resourceId}`;
      setSelectedDeskForBooking({ id: searchParams.resourceId, name: deskName });
      setSelectedMeetingRoomForBooking(null);
    }
  }, [office, searchParams.resourceId]);

  const preferredViewMode = useGetPreferredViewMode(currentUser?.uid);
  useEffect(() => {
    setViewMode(preferredViewMode);
  }, [preferredViewMode]);

  const handleFloorChange = (floorId: string) => {
    userPickedFloor.current = true;
    setSelectedFloorId(floorId);
    if (!effectiveOfficeId) return;
    try {
      localStorage.setItem(`robinhoot.floor.${effectiveOfficeId}`, floorId);
    } catch {
      // Private browsing can block storage. The choice still lasts for this visit.
    }
  };

  const handleViewModeChange = (newMode: ViewMode) => {
    setViewMode(newMode);
    if (currentUser?.uid) {
      savePreferredViewMode(currentUser.uid, newMode);
    }
  };

  const getWeekDays = (date: Date) => {
    const startOfCurrentWeek = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => toNoonLocal(addDays(startOfCurrentWeek, i)));
  };

  const weekDays = getWeekDays(selectedDate);

  const { weekStart, weekEnd } = useMemo(() => {
    const start = toNoonLocal(startOfWeek(selectedDate, { weekStartsOn: 1 }));
    const end = toNoonLocal(endOfWeek(selectedDate, { weekStartsOn: 1 }));
    return { weekStart: start, weekEnd: end };
  }, [selectedDate.getTime()]);

  // Use unified hook that fetches all bookings in one query and separates them
  const {
    deskBookings: weekBookings,
    meetingRoomBookings: allMeetingRoomBookings,
    overflowBookings: allOverflowBookings,
    loading: bookingsLoading,
    error: bookingsError,
  } = useGetAllBookings(effectiveOfficeId || '', weekStart, weekEnd, officeTimezone);

  const selectedDateKey = useMemo(
    () => getDateKeyInTimezone(selectedDate, officeTimezone),
    [selectedDate, officeTimezone]
  );
  const floors = useMemo(() => resolveOfficeFloors(office), [office]);

  useEffect(() => {
    if (!office || floors.length === 0) return;
    if (floorChoiceOfficeId.current !== office.id) {
      floorChoiceOfficeId.current = office.id;
      userPickedFloor.current = false;
    }
    if (userPickedFloor.current) return;

    const ownDeskBooking = weekBookings.find(
      booking =>
        booking.bookingType === 'DESK' &&
        !booking.isGuest &&
        booking.userEmail === currentUser?.email &&
        Boolean(booking.resourceId) &&
        isTimestampOnDateKey(booking.date, selectedDateKey, officeTimezone)
    );
    const bookedFloor = ownDeskBooking?.resourceId
      ? findFloorForResource(floors, ownDeskBooking.resourceId)
      : undefined;
    let storedFloorId: string | null = null;
    try {
      storedFloorId = localStorage.getItem(`robinhoot.floor.${office.id}`);
    } catch {
      storedFloorId = null;
    }
    const storedFloor = floors.find(floor => floor.id === storedFloorId);
    setSelectedFloorId(bookedFloor?.id ?? storedFloor?.id ?? floors[0].id);
  }, [office, floors, weekBookings, selectedDateKey, officeTimezone, currentUser?.email]);

  const floorAvailability = useMemo(
    () => getFreeDesksByFloor(floors, weekBookings, selectedDate, officeTimezone),
    [floors, weekBookings, selectedDate, officeTimezone]
  );

  const dayDeskBookings = useStableDayBookings(weekBookings, selectedDateKey, officeTimezone);
  const meetingRoomBookingsData = useStableDayBookings(
    allMeetingRoomBookings,
    selectedDateKey,
    officeTimezone
  );

  // Conditionally determine officeId for fetching events
  const officeIdForEvents = officeLayout?.enableEvents !== false ? effectiveOfficeId : '';
  const { Events } = useGetEvents(officeIdForEvents || '', weekStart, weekEnd);

  const weekMarks = useMemo(
    () =>
      getDayMarks(
        [...weekBookings, ...allOverflowBookings],
        Events,
        currentUser?.email,
        officeTimezone
      ),
    [weekBookings, allOverflowBookings, Events, currentUser?.email, officeTimezone]
  );

  const EventsByDate = useMemo(() => {
    const eventMap = new Map<string, typeof Events>();
    Events.forEach(event => {
      for (const dateKey of getEventDateKeys(event)) {
        eventMap.set(dateKey, [...(eventMap.get(dateKey) ?? []), event]);
      }
    });
    return eventMap;
  }, [Events]);

  // Meeting room booking handlers
  const handleBookMeetingRoom = (
    resourceId: string | null,
    startTime: Date,
    endTime: Date,
    isGuest?: boolean,
    guestName?: string,
    guestEmail?: string
  ) => {
    if (!resourceId) return;
    const roomId = resourceId;
    if (!currentUser) return;
    useBookMeetingRoom(
      roomId,
      effectiveOfficeId || '',
      currentUser.email || '',
      currentUser.displayName || 'User',
      currentUser.photoURL || '',
      startTime,
      endTime,
      selectedDate,
      officeTimezone,
      {
        onSuccess: () => {
          // The Firebase listener should automatically update the data
        },
        onError: error => {
          console.error('Meeting room booking failed:', error);
        },
      },
      isGuest,
      guestName,
      guestEmail
    )();
    // Keep the side panel open after booking so users can see their booking and book additional slots
  };

  const handleCancelMeetingRoomCard = (bookingId: string) => {
    if (!currentUser || !bookingId) return;

    // Parse the booking ID to get dateTimestamp and actual booking ID
    // Format: "dateTimestamp:actualBookingId"
    if (bookingId.includes(':')) {
      const [dateTimestamp, actualBookingId] = bookingId.split(':');
      useCancelMeetingRoomBook(actualBookingId, effectiveOfficeId || '', parseInt(dateTimestamp))();
    }
  };

  // Desk booking handlers
  const showFacilitiesNotify =
    !!appUser?.isRemoteWorker && !!office?.layout?.enableFacilitiesNotification;

  const handleBookDesk = (
    resourceId: string | null,
    startTime: Date,
    endTime: Date,
    isGuest?: boolean,
    guestName?: string,
    guestEmail?: string
  ) => {
    if (!currentUser || !resourceId) return;
    const deskId = resourceId;
    useBookDesk(
      deskId,
      effectiveOfficeId || '',
      currentUser.email || '',
      currentUser.displayName || 'User',
      currentUser.photoURL || '',
      startTime,
      endTime,
      selectedDate,
      officeTimezone,
      {
        onError: error => {
          console.error('Desk booking failed:', error);
        },
      },
      isGuest,
      guestName,
      guestEmail
    )();
    // Keep the side panel open after booking so users can see their booking and book additional slots
  };

  const handleCancelDeskCard = (bookingId: string) => {
    if (!currentUser || !bookingId) return;

    // Parse the booking ID to get dateTimestamp and actual booking ID
    // Format: "dateTimestamp:actualBookingId"
    if (bookingId.includes(':')) {
      const [dateTimestamp, actualBookingId] = bookingId.split(':');
      useCancelDeskBook(actualBookingId, effectiveOfficeId || '', parseInt(dateTimestamp))();
    }
  };

  // Overflow booking handlers
  const handleBookOverflow = (
    _resourceId: string | null, // Not used for overflow bookings, but required by BookingPanel interface
    startTime: Date,
    endTime: Date,
    isGuest?: boolean,
    guestName?: string,
    guestEmail?: string
  ) => {
    if (!currentUser) return;
    useBookOverflow(
      effectiveOfficeId || '',
      currentUser.email || '',
      currentUser.displayName || 'User',
      currentUser.photoURL || '',
      selectedDate,
      officeTimezone,
      {
        onSuccess: () => {
          // The Firebase listener should automatically update the data
        },
        onError: error => {
          console.error('Overflow booking failed:', error);
        },
      },
      isGuest,
      guestName,
      startTime,
      endTime,
      guestEmail
    )();
  };

  const handleCancelOverflowCard = (bookingId: string) => {
    if (!currentUser || !bookingId) return;

    // Parse the booking ID to get dateTimestamp and actual booking ID
    // Format: "dateTimestamp:actualBookingId"
    if (bookingId.includes(':')) {
      const [dateTimestamp, actualBookingId] = bookingId.split(':');
      useCancelDeskBook(actualBookingId, effectiveOfficeId || '', parseInt(dateTimestamp))();
    }
  };

  const overflowBookingsForDate = useMemo(() => {
    return allOverflowBookings.filter(booking =>
      isTimestampOnDateKey(booking.date, selectedDateKey, officeTimezone)
    );
  }, [allOverflowBookings, selectedDateKey, officeTimezone]);

  // Prepare data for meeting room booking panel
  const showMeetingRoomBookingPanel =
    selectedMeetingRoomForBooking && officeLayout?.placedMeetingRooms;
  const selectedRoom = showMeetingRoomBookingPanel
    ? Object.entries(officeLayout.placedMeetingRooms || {}).find(
        ([id]) => id === selectedMeetingRoomForBooking!.id
      )
    : null;

  const roomWithName = selectedRoom
    ? {
        ...selectedRoom[1],
        id: selectedRoom[0],
        name: selectedMeetingRoomForBooking!.name,
      }
    : null;

  const roomBookings = selectedMeetingRoomForBooking
    ? meetingRoomBookingsData.filter(
        b => b.bookingType === 'MEETING_ROOM' && b.resourceId === selectedMeetingRoomForBooking.id
      )
    : [];

  // Prepare data for desk booking panel
  const showDeskBookingPanel = selectedDeskForBooking && officeLayout?.placedDesks;
  const selectedDeskData = showDeskBookingPanel
    ? Object.entries(officeLayout.placedDesks || {}).find(
        ([id]) => id === selectedDeskForBooking!.id
      )
    : null;

  const deskWithName = selectedDeskData
    ? {
        ...selectedDeskData[1],
        id: selectedDeskData[0],
        name: selectedDeskForBooking!.name,
      }
    : null;

  const deskBookings = selectedDeskForBooking
    ? weekBookings.filter(
        b =>
          b.bookingType === 'DESK' &&
          b.resourceId === selectedDeskForBooking.id &&
          isTimestampOnDateKey(b.date, selectedDateKey, officeTimezone)
      )
    : [];

  const officeIsFullyBooked = useMemo(() => {
    return isOfficeFullyBooked(floors, weekBookings, selectedDate, officeTimezone);
  }, [floors, weekBookings, selectedDate, officeTimezone]);

  // Check if current user already has an overflow booking for the selected date
  const currentUserHasOverflowBooking = useMemo(() => {
    if (!currentUser) return false;
    return overflowBookingsForDate.some(
      booking => booking.userEmail === currentUser.email && !booking.isGuest
    );
  }, [overflowBookingsForDate, currentUser]);

  // Get unique overflow bookings (deduplicate by userEmail for non-guests, by guestName for guests)
  const uniqueOverflowBookings = useMemo(() => {
    const seen = new Set<string>();
    return overflowBookingsForDate.filter(booking => {
      const key = booking.isGuest
        ? `guest:${booking.guestName || ''}`
        : `user:${booking.userEmail}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [overflowBookingsForDate]);

  const allowMapToggle = office?.layout?.useOfficeMap;

  const isLoading = officeLoading || bookingsLoading || preferredOfficeLoading;

  if (officeError) {
    return (
      <ErrorState
        title="Failed to load office"
        description="We couldn't load the office data. Please try again."
      />
    );
  }

  if (bookingsError) {
    return (
      <ErrorState
        title="Failed to load bookings"
        description="We couldn't load the booking data. Please try again."
      />
    );
  }

  if (isLoading) {
    return <BookingPageSkeleton />;
  }

  return (
    <>
      <div className="px-2 sm:px-4 py-2">
        <div className="m-auto max-w-7xl 2xl:max-w-[2000px]">
          {effectiveOfficeId && office && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
              {/* Left Column - Calendar & Navigation */}
              <div className="lg:col-span-5 xl:col-span-4 2xl:col-span-3">
                <Card className="border-0 shadow-sm lg:sticky lg:top-4">
                  <div className="space-y-5">
                    <header className="flex min-h-8 items-center gap-1">
                      <h2 className="min-w-0 truncate text-lg font-semibold">
                        {office?.name || 'Office'}
                      </h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 shrink-0 p-0 dark:hover:bg-accent"
                        onClick={() => setOfficeInfoDialogOpen(true)}
                        aria-label="Office information"
                        title="Office information"
                      >
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <SuppliesAndSuggestionsDialog
                        key={office.id}
                        officeId={office.id}
                        officeName={office.name}
                        officeTimezone={officeTimezone}
                        canManageSupplies={canRemoveOthers}
                        suppliesEmail={office.layout?.suppliesEmail}
                      />
                      {allowMapToggle && (
                        <div className="ml-auto shrink-0 pl-2">
                          <ViewModeSwitch value={viewMode} onChange={handleViewModeChange} />
                        </div>
                      )}
                    </header>

                    <div className="space-y-3">
                      <WeekCalendar
                        selectedDate={selectedDate}
                        weekDays={weekDays}
                        onSelect={setSelectedDate}
                        marks={weekMarks}
                        officeId={effectiveOfficeId || ''}
                        officeTimezone={officeTimezone}
                        currentUserEmail={currentUser?.email}
                        showEvents={officeLayout?.enableEvents !== false}
                      />
                      {floors.length > 1 && (
                        <FloorPicker
                          floors={floors}
                          selectedFloorId={selectedFloorId}
                          floorAvailability={floorAvailability}
                          onChange={handleFloorChange}
                        />
                      )}
                    </div>

                    <section
                      aria-label="Selected day"
                      className="space-y-3 border-t border-border/60 pt-4"
                    >
                      <BookingInfoBox
                        weekBookings={weekBookings}
                        overflowBookings={allOverflowBookings}
                        selectedDate={selectedDate}
                        officeId={effectiveOfficeId || ''}
                        officeTimezone={officeTimezone}
                        viewMode={viewMode}
                        onViewModeChange={handleViewModeChange}
                        showMapToggle={false}
                        sidebarMode={true}
                        events={EventsByDate.get(selectedDateKey) || []}
                        enableEvents={officeLayout?.enableEvents !== false}
                      />
                      {(officeIsFullyBooked || overflowBookingsForDate.length > 0) && (
                        <DesklessBookings
                          bookings={uniqueOverflowBookings}
                          isFullyBooked={officeIsFullyBooked}
                          currentUserEmail={currentUser?.email}
                          hasOwnBooking={currentUserHasOverflowBooking}
                          onOpen={() => setShowOverflowBookingPanel(true)}
                          officeTimezone={officeTimezone}
                        />
                      )}
                    </section>
                  </div>
                </Card>
              </div>

              {/* Right Column - Main Content */}
              <div className="lg:col-span-7 xl:col-span-8 2xl:col-span-9">
                <AnimatePresence mode="wait">
                  <motion.div
                    ref={motionDivRef}
                    key={`${effectiveOfficeId}-${selectedFloorId}-${viewMode}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Booking
                      officeId={effectiveOfficeId}
                      selectedDate={selectedDate}
                      auth={auth}
                      viewMode={viewMode}
                      onMeetingRoomSelect={setSelectedMeetingRoomForBooking}
                      onDeskSelect={setSelectedDeskForBooking}
                      _selectedRoomId={selectedMeetingRoomForBooking?.id}
                      _selectedDeskId={selectedDeskForBooking?.id}
                      officeTimezone={officeTimezone}
                      floorId={selectedFloorId}
                      floorName={floors.find(floor => floor.id === selectedFloorId)?.name}
                      deskBookings={dayDeskBookings}
                      meetingRoomBookings={meetingRoomBookingsData}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )}
          {!office && effectiveOfficeId && !officeLoading && !bookingsLoading && (
            <Card className="m-auto max-w-4xl p-4">
              <h3 className="text-base sm:text-lg font-bold text-center font-serif">
                Office data could not be loaded for the preferred office.
              </h3>
            </Card>
          )}
        </div>
      </div>

      {/* Meeting Room Booking Side Panel */}
      <AnimatePresence>
        {showMeetingRoomBookingPanel && roomWithName && (
          <>
            {/* Subtle backdrop overlay with fade animation */}
            <motion.div
              className="fixed inset-0 bg-black/10 z-30"
              onPointerDown={() => setSelectedMeetingRoomForBooking(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="fixed top-0 bottom-0 right-0 w-full sm:w-106 z-40"
              {...SIDE_PANEL_SLIDE}
            >
              <BookingPanel
                resource={roomWithName}
                resourceType="meeting-room"
                selectedDate={selectedDate}
                existingBookings={roomBookings}
                currentUserEmail={currentUser?.email || ''}
                onBook={handleBookMeetingRoom}
                onCancel={handleCancelMeetingRoomCard}
                onClose={() => setSelectedMeetingRoomForBooking(null)}
                initialStartTime={activeInitialBookingParams.startTime}
                initialEndTime={activeInitialBookingParams.endTime}
                officeId={effectiveOfficeId}
                enableGuestBooking={officeLayout?.enableGuestBooking !== false}
                officeTimezone={officeTimezone}
                canRemoveOthers={canRemoveOthers}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desk Booking Side Panel */}
      <AnimatePresence>
        {showDeskBookingPanel && deskWithName && (
          <>
            {/* Subtle backdrop overlay with fade animation */}
            <motion.div
              className="fixed inset-0 bg-black/10 z-30"
              onPointerDown={() => setSelectedDeskForBooking(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="fixed top-0 bottom-0 right-0 w-full sm:w-106 z-40"
              {...SIDE_PANEL_SLIDE}
            >
              <BookingPanel
                resource={deskWithName}
                resourceType="desk"
                selectedDate={selectedDate}
                existingBookings={deskBookings}
                currentUserEmail={currentUser?.email || ''}
                onBook={handleBookDesk}
                onCancel={handleCancelDeskCard}
                onClose={() => setSelectedDeskForBooking(null)}
                initialStartTime={activeInitialBookingParams.startTime}
                initialEndTime={activeInitialBookingParams.endTime}
                enableGuestBooking={officeLayout?.enableGuestBooking !== false}
                officeId={effectiveOfficeId}
                officeTimezone={officeTimezone}
                showFacilitiesNotify={showFacilitiesNotify}
                facilitiesDeskBookings={weekBookings}
                facilitiesRefreshKey={weekBookings.length}
                canRemoveOthers={canRemoveOthers}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Overflow Booking Side Panel */}
      <AnimatePresence>
        {showOverflowBookingPanel && (
          <>
            {/* Subtle backdrop overlay with fade animation */}
            <motion.div
              className="fixed inset-0 bg-black/10 z-30"
              onPointerDown={() => setShowOverflowBookingPanel(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="fixed top-0 bottom-0 right-0 w-full sm:w-106 z-40"
              {...SIDE_PANEL_SLIDE}
            >
              <BookingPanel
                resourceType="overflow"
                selectedDate={selectedDate}
                existingBookings={overflowBookingsForDate}
                currentUserEmail={currentUser?.email || ''}
                onBook={handleBookOverflow}
                onCancel={handleCancelOverflowCard}
                onClose={() => setShowOverflowBookingPanel(false)}
                enableGuestBooking={officeLayout?.enableGuestBooking !== false}
                officeId={effectiveOfficeId}
                officeTimezone={officeTimezone}
                canRemoveOthers={canRemoveOthers}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Office Info Dialog */}
      {office && (
        <OfficeInfoDialog
          office={office}
          floors={floors}
          selectedFloorId={selectedFloorId}
          open={officeInfoDialogOpen}
          onOpenChange={setOfficeInfoDialogOpen}
        />
      )}
    </>
  );
}
